// Maintenance module — shared vocabulary. Stage colours, duration wording,
// money formatting and the machine-identity helpers every surface reuses.
// See MAINTENANCE_MODULE_PLAN.md §8 and maintenance_mock_pipeline.html (v3).

// Stage accents — the ERP's calm hues, locked 18-Aug. Red/amber stay reserved
// for true alerts (a stopped machine's clock), never for stage identity.
export const MAINT_STAGES = [
  { key: "received", path: "/maintenance/received", label: "Breakdowns Received", short: "Received", color: "#3b82f6" },
  { key: "repair",   path: "/maintenance/under-repair", label: "Under Repair",    short: "Repair",   color: "#f97316" },
  { key: "handover", path: "/maintenance/handover",     label: "Handover",        short: "Handover", color: "#14b8a6" },
  { key: "complete", path: "/maintenance/complete",     label: "Complete",        short: "Complete", color: "#64748b" },
];
// Phase 2 — the three frosted buttons that sit AFTER the pipeline groove,
// behind a hairline divider. They are not stages: the groove still ends at
// Complete. `countKey` names the /maintenance/counts field each badge reads.
export const MAINT_EXTRAS = [
  { key: "pm",       path: "/maintenance/pm",       label: "PM",       full: "Preventive maintenance",      countKey: "pm_overdue" },
  { key: "spares",   path: "/maintenance/spares",   label: "Spares",   full: "Spare orders",                countKey: "spares_pending" },
  { key: "requests", path: "/maintenance/requests", label: "Requests", full: "Requests & pending points",   countKey: "requests_open" },
];

export const MAINT_STAGE_BY_KEY = Object.fromEntries(MAINT_STAGES.map((s) => [s.key, s]));

// Machine categories come from MACHINE_DETAIL_COE.xlsx in shouty sheet case;
// these are the words the shop floor actually reads.
export const CATEGORY_LABEL = {
  "TURNING CENTER": "Turning center",
  "TURNMILL CENTER Y AXIS": "Turnmill · Y-axis",
  "TURNMILL CENTER": "Turnmill center",
  "TURNMILL WITH MILL SPINDLE": "Turnmill · mill spindle",
  "VERTICAL MILLING MACHINES": "Milling / 5-axis",
};
export const categoryLabel = (c) => CATEGORY_LABEL[c] || (c ? c.charAt(0) + c.slice(1).toLowerCase() : "");

// One duration vocabulary across the module (mirrors _maint_human_duration on
// the server): 34d 6h · 7h 05m · 25m.
export function humanDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m`;
}

// The LIVE variant — same vocabulary, with the seconds still running. Used
// wherever a clock is genuinely ticking right now (a machine that is down, the
// longest-open spell): a stopped machine should feel like it is costing you
// something this second. Never used for a finished record — those are settled
// numbers and must not twitch.
export function humanDurationLive(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  if (d) return `${d}d ${h}h ${p(m)}m ${p(sec)}s`;
  if (h) return `${h}h ${p(m)}m ${p(sec)}s`;
  if (m) return `${m}m ${p(sec)}s`;
  return `${sec}s`;
}

// "2h 10m ago" — the same wording, past tense.
export function agoFrom(iso, nowMs) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return `${humanDuration(((nowMs || Date.now()) - t) / 1000)} ago`;
}

export function secondsSince(iso, nowMs) {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, ((nowMs || Date.now()) - t) / 1000);
}

// A stopped machine turns red once it passes four CLOCKED hours down — the
// same line the alert email is sent on, so what the row shows and what the
// chairman is emailed about mean the same thing. (There is still no ETA /
// promise mechanism — this is an attention signal, not a deadline.)
export const DOWN_ALERT_SECONDS = 4 * 3600;

// ── The clock's grace period ────────────────────────────────────────────────
// Nothing starts counting the moment a breakdown is reported: a stopped
// machine gets 10 minutes for maintenance to take it up, one that is still
// producing gets 3 hours. The server stamps `clock_start_at` on every ticket;
// these read from it so the page and the server never disagree.

/** Seconds ON THE CLOCK right now — 0 while the ticket is still in grace. */
export function clockedSeconds(ticket, nowMs) {
  if (!ticket) return 0;
  // A closed breakdown is a settled number; it must never keep moving.
  if (ticket.closed_at || !ticket.clock_start_at) return ticket.down_seconds || 0;
  return Math.max(0, ((nowMs || Date.now()) - Date.parse(ticket.clock_start_at)) / 1000);
}

/** Seconds still to run before the clock starts — 0 once it has. */
export function graceRemaining(ticket, nowMs) {
  if (!ticket?.clock_start_at || ticket.closed_at) return 0;
  return Math.max(0, (Date.parse(ticket.clock_start_at) - (nowMs || Date.now())) / 1000);
}

/** What this breakdown has cost SO FAR, ticking — null when it can't be known. */
export function liveCost(ticket, nowMs) {
  if (!ticket) return null;
  if (ticket.rate_inr_per_hour == null) return ticket.cost_inr ?? null;
  return (ticket.rate_inr_per_hour * clockedSeconds(ticket, nowMs)) / 3600;
}

// "35th day" — long spells read as days, the way the factory talks about them.
export function dayCountLabel(seconds) {
  const days = Math.floor((Number(seconds) || 0) / 86400);
  if (days < 1) return "";
  const n = days + 1;
  const suffix = n % 10 === 1 && n % 100 !== 11 ? "st"
    : n % 10 === 2 && n % 100 !== 12 ? "nd"
    : n % 10 === 3 && n % 100 !== 13 ? "rd" : "th";
  return `${n}${suffix} day`;
}

export function formatINR(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹ ${Math.round(n).toLocaleString("en-IN")}`;
}

export function dateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const yesterday = new Date(today.getTime() - 86400000);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export const initialsOf = (name) =>
  String(name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";
